import { useMemo } from 'react';
import type { DayBucket } from '@busy/api';
import { useT } from '../../lib/i18n';
import { useUI } from '../../store/ui';

// Computed insights from the range data. Picks 1-3 highlights from a candidate
// list, prioritized by signal strength. Mirrors the demo's "FOR YOU" panel —
// it surfaces something useful without requiring the barber to read the chart.

type InsightKind = 'metric' | 'reflective';

type Insight = {
  kind: InsightKind;
  weight: number;
  key: string;
  vars: Record<string, string | number>;
};

export function InsightsCard({ days }: { days: DayBucket[] }) {
  const t = useT();
  const language = useUI((s) => s.language);
  const insights = useMemo(() => buildInsights(days, language), [days, language]);

  if (insights.length === 0) {
    return (
      <div className="rounded-2xl border border-line bg-surface p-4">
        <div className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-ink-500 inline-flex items-center gap-1.5 mb-2">
          <SparkIcon className="w-3 h-3" />
          {t('dash.insights.eyebrow')}
        </div>
        <p className="text-sm text-ink-500">{t('dash.insights.empty')}</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-line bg-surface p-4 space-y-3">
      <div className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-ink-500 inline-flex items-center gap-1.5">
        <SparkIcon className="w-3 h-3" />
        {t('dash.insights.eyebrow')}
      </div>
      <ul className="space-y-2.5">
        {insights.slice(0, 3).map((ins, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <div className="w-6 h-6 rounded-md bg-gold-50 dark:bg-gold-500/15 grid place-items-center shrink-0 mt-0.5">
              {ins.kind === 'metric' ? (
                <TrendIcon className="w-3.5 h-3.5 text-[#93701c] dark:text-gold-300" />
              ) : (
                <LightIcon className="w-3.5 h-3.5 text-[#93701c] dark:text-gold-300" />
              )}
            </div>
            <div className="text-sm leading-relaxed">{t(ins.key, ins.vars)}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Pure function — exported so it can be unit-tested if we ever set that up.
// Returns translation keys + vars; the caller resolves through their i18n layer.
export function buildInsights(days: DayBucket[], language: 'en' | 'es' = 'en'): Insight[] {
  const out: Insight[] = [];
  const past = days.filter((d) => d.visits > 0);
  if (past.length === 0) return out;
  const locale = language === 'es' ? 'es-US' : 'en-US';

  // Best day in the range
  const best = past.reduce((a, b) => (b.revenue_cents > a.revenue_cents ? b : a), past[0]);
  if (best.revenue_cents > 0) {
    const date = new Date(best.ms);
    const dayName = date.toLocaleDateString(locale, { weekday: 'long' });
    out.push({
      kind: 'metric',
      weight: 8,
      key: best.visits === 1 ? 'dash.insights.bestDay_one' : 'dash.insights.bestDay_other',
      vars: {
        day: dayName,
        date: formatDate(best.ms, locale),
        rev: (best.revenue_cents / 100).toFixed(0),
        count: best.visits,
      },
    });
  }

  // Total revenue + cut count
  const totalRev = days.reduce((s, d) => s + d.revenue_cents, 0);
  const totalVisits = days.reduce((s, d) => s + d.visits, 0);
  if (totalVisits >= 3) {
    out.push({
      kind: 'metric',
      weight: 7,
      key: totalVisits === 1 ? 'dash.insights.totals_one' : 'dash.insights.totals_other',
      vars: {
        count: totalVisits,
        rev: (totalRev / 100).toFixed(0),
        avg: (totalRev / 100 / totalVisits).toFixed(0),
      },
    });
  }

  // Compare last 7 days vs prior 7 (only when range >= 14 days of data)
  if (days.length >= 14) {
    const recent = days.slice(-7);
    const prior = days.slice(-14, -7);
    const recentRev = recent.reduce((s, d) => s + d.revenue_cents, 0);
    const priorRev = prior.reduce((s, d) => s + d.revenue_cents, 0);
    if (priorRev > 0) {
      const pct = Math.round(((recentRev - priorRev) / priorRev) * 100);
      if (Math.abs(pct) >= 10) {
        out.push({
          kind: 'metric',
          weight: 9,
          key: pct > 0 ? 'dash.insights.trendUp' : 'dash.insights.trendDown',
          vars: { pct: Math.abs(pct) },
        });
      }
    }
  }

  // Quiet day pattern — find weekday with lowest avg
  if (past.length >= 7) {
    const byDow: number[][] = [[], [], [], [], [], [], []];
    for (const d of past) byDow[new Date(d.ms).getDay()].push(d.visits);
    let worstDow = -1;
    let worstAvg = Infinity;
    for (let i = 0; i < 7; i++) {
      if (byDow[i].length === 0) continue;
      const avg = byDow[i].reduce((a, b) => a + b, 0) / byDow[i].length;
      if (avg < worstAvg) {
        worstAvg = avg;
        worstDow = i;
      }
    }
    if (worstDow >= 0 && worstAvg < 2) {
      // Use the active language's full day name from Intl, matching the rest of
      // the dashboard's date formatting.
      const sample = new Date();
      sample.setDate(sample.getDate() - sample.getDay() + worstDow);
      const dayName = sample.toLocaleDateString(locale, { weekday: 'long' });
      out.push({
        kind: 'reflective',
        weight: 5,
        key: 'dash.insights.quietDay',
        vars: { day: dayName, avg: worstAvg.toFixed(1) },
      });
    }
  }

  // Tip-rate by day-of-week — surfaces the weekday whose tip rate
  // beats the overall by a meaningful margin. Threshold tuning:
  //   • Per-day visit floor: 3 cuts. Below that the sample's too thin
  //     for "Fridays tip 30%" to mean anything.
  //   • Difference floor: 5 percentage points. Anything tighter is
  //     just noise — barber asks "is this a real pattern?" and the
  //     answer is no.
  // The insight reads "Fridays tip the best — 28% (vs 21% overall)."
  // Cross-day comparison drives the actionable read; the absolute %
  // gives the barber the number they want to retell.
  if (past.length >= 14) {
    type DowAcc = { tip: number; revenue: number; visits: number };
    const byDowTip: DowAcc[] = Array.from({ length: 7 }, () => ({
      tip: 0,
      revenue: 0,
      visits: 0,
    }));
    let totalTip = 0;
    let totalRev = 0;
    for (const d of past) {
      const dow = new Date(d.ms).getDay();
      byDowTip[dow]!.tip += d.tip_cents;
      byDowTip[dow]!.revenue += d.revenue_cents;
      byDowTip[dow]!.visits += d.visits;
      totalTip += d.tip_cents;
      totalRev += d.revenue_cents;
    }
    if (totalTip > 0 && totalRev > 0) {
      const overallPct = (totalTip / totalRev) * 100;
      let bestDow = -1;
      let bestPct = -1;
      for (let i = 0; i < 7; i++) {
        const acc = byDowTip[i]!;
        if (acc.visits < 3 || acc.revenue === 0) continue;
        const pct = (acc.tip / acc.revenue) * 100;
        if (pct > bestPct) {
          bestPct = pct;
          bestDow = i;
        }
      }
      if (bestDow >= 0 && bestPct - overallPct >= 5) {
        const sample = new Date();
        sample.setDate(sample.getDate() - sample.getDay() + bestDow);
        const dayName = sample.toLocaleDateString(locale, { weekday: 'long' });
        out.push({
          kind: 'metric',
          weight: 6,
          key: 'dash.insights.bestTipDay',
          vars: {
            day: dayName,
            dayPct: Math.round(bestPct),
            overallPct: Math.round(overallPct),
          },
        });
      }
    }
  }

  // Sort by weight desc, metric > reflective on tie
  out.sort((a, b) => b.weight - a.weight || (a.kind === 'metric' ? -1 : 1));
  return out;
}

function formatDate(ms: number, locale: string): string {
  return new Date(ms).toLocaleDateString(locale, { month: 'short', day: 'numeric' });
}

function SparkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2 14.5 8.5 21 11l-6.5 2.5L12 20l-2.5-6.5L3 11l6.5-2.5L12 2z" />
    </svg>
  );
}
function TrendIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  );
}
function LightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 18h6" />
      <path d="M10 22h4" />
      <path d="M12 2a7 7 0 0 0-4 12.74V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.26A7 7 0 0 0 12 2z" />
    </svg>
  );
}
